import { useEffect, useRef, useState } from "react";
import ReactPlayer from "react-player";
import { api } from "@/lib/api";

const RATES = [1, 1.25, 1.5];
const SAVE_EVERY = 12;
const TOLERANCE = 1.25;

export default function StudentVideoLessonPlayer({ lesson }) {
  const playerRef = useRef(null);
  const lastPos = useRef(0);
  const lastTs = useRef(Date.now());
  const pending = useRef(0);
  const playing = useRef(false);
  const [rate, setRate] = useState(1);
  const [prog, setProg] = useState({ progress_percent: 0, valid_watched_seconds: 0, duration_seconds: 0, completed: false });

  useEffect(() => {
    api.get(`/student/portal/lessons/${lesson.id}/progress/me`).then(({ data }) => setProg(data)).catch(() => {});
  }, [lesson.id]);

  const save = async (force = false, visibleOverride = null) => {
    if (pending.current <= 0 && !force) return;
    const delta = pending.current;
    pending.current = 0;
    const el = playerRef.current;
    const duration = el?.duration || prog.duration_seconds || 0;
    if (!duration) return;
    try {
      const { data } = await api.post(`/student/portal/lessons/${lesson.id}/progress`, {
        duration_seconds: duration,
        current_position_seconds: el?.currentTime || 0,
        valid_watched_seconds_delta: delta,
        playback_rate: rate,
        is_playing: playing.current,
        visible: visibleOverride == null ? !document.hidden : visibleOverride,
        video_url: lesson.video_url,
      });
      setProg(data);
    } catch (_) {}
  };

  const tick = () => {
    const el = playerRef.current;
    if (!el) return;
    const cur = el.currentTime || 0;
    const dur = el.duration || 0;
    const now = Date.now();
    if (playing.current && !document.hidden && dur > 0) {
      const actual = cur - lastPos.current;
      const expected = ((now - lastTs.current) / 1000) * rate + TOLERANCE;
      if (actual > 0 && actual <= expected) pending.current += actual;
    }
    lastPos.current = cur;
    lastTs.current = now;
    if (pending.current >= SAVE_EVERY) save();
  };

  const play = () => {
    playing.current = true;
    lastTs.current = Date.now();
    lastPos.current = playerRef.current?.currentTime || 0;
  };

  const pause = () => {
    save(true, true);
    playing.current = false;
  };

  useEffect(() => {
    const vis = () => {
      if (document.hidden) {
        save(true, true);
        playing.current = false;
      }
    };
    document.addEventListener("visibilitychange", vis);
    return () => {
      document.removeEventListener("visibilitychange", vis);
      save(true, true);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const watched = Math.floor((prog.valid_watched_seconds || 0) / 60);
  const needed = prog.duration_seconds ? Math.round((prog.duration_seconds * 0.9) / 60) : 0;
  const status = prog.completed ? "Concluída" : prog.progress_percent > 0 ? "Em andamento" : "Não iniciada";
  const badge = prog.completed ? "doxa-badge-success" : prog.progress_percent > 0 ? "doxa-badge-warning" : "doxa-badge-neutral";

  return (
    <div className="mt-4" data-testid="video-lesson-player">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <p className="text-xs uppercase tracking-wide text-[#6B655E]">Vídeo da aula</p>
        <div className="flex items-center gap-1">
          <span className="text-xs text-[#6B655E] mr-1">Velocidade:</span>
          {RATES.map((r) => (
            <button key={r} onClick={() => setRate(r)} className={`text-xs px-2 py-1 rounded-lg ${rate === r ? "bg-[#F75A1B] text-white" : "bg-[#F6EFE4] text-[#4A4640]"}`}>{r}x</button>
          ))}
        </div>
      </div>
      <div className="relative w-full rounded-xl overflow-hidden bg-black" style={{ paddingTop: "56.25%" }}>
        <ReactPlayer ref={playerRef} src={lesson.video_url} controls playbackRate={rate} onPlay={play} onPause={pause} onEnded={() => save(true, true)} onTimeUpdate={tick} style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />
      </div>
      <div className="mt-3 rounded-xl border border-[#EBE5DB] bg-[#FAF7F1] p-3">
        <div className="flex items-center justify-between"><p className="font-medium text-sm">Progresso da aula</p><span className={`doxa-badge ${badge}`}>{status}</span></div>
        <div className="w-full h-2 bg-[#EBE5DB] rounded-full mt-2 overflow-hidden"><div className="h-full bg-[#F75A1B]" style={{ width: `${Math.min(prog.progress_percent || 0, 100)}%` }} /></div>
        <p className="text-xs text-[#6B655E] mt-2">Progresso: {prog.progress_percent || 0}% · {watched} min de {needed} min necessários</p>
      </div>
    </div>
  );
}
